import React, { useMemo, useState } from 'react';

import {
  Button,
  Checkbox,
  Group,
  Input,
  LoadingOverlay,
  Stack,
  Text,
  TextInput,
  Textarea,
  Title,
  px,
  useMantineTheme,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDisclosure, useShallowEffect } from '@mantine/hooks';
import PageHeader from 'components/PageHeader';
import ImageUpload from 'components/upload/ImageUpload';
import { useAuth } from 'context/auth-context';
import useGym from 'hooks/gym/useGym';
import useSalesPortalConfiguration from 'hooks/salesPortalConfiguration/useSalesPortalConfiguration';
import useUpdateSalesPortalConfiguration from 'hooks/salesPortalConfiguration/useUpdateSalesPortalConfiguration';
import notificationHandler from 'utils/notifications';

interface FormValues {
  enabled: boolean;
  slug: string;
  homePageText: string;
  // No input renders these two any more (see ENG-2598 below), but they must stay on the form:
  // handleSubmit spreads `values` into the update payload, so dropping them would post empty
  // strings and wipe the ids the gyms that already filled them in have saved.
  facebookTracking: string;
  googleTracking: string;
  file: File | null;
}

const SalesPortalConfiguration = (): JSX.Element => {
  const theme = useMantineTheme();

  const { session, hasPerm } = useAuth();
  const permission = hasPerm('SalesPortalMutate');

  const [uploading, setUploading] = useState<boolean>(false);
  const [logo, setLogo] = useState<string | null>(null);
  const [isEditable, { open, close }] = useDisclosure(false);

  const { data: gymData } = useGym(session?.gym?.gymId);

  const updateMutation = useUpdateSalesPortalConfiguration();
  const { successNotification, errorNotification } = notificationHandler();
  const { data, isLoading, isSuccess } = useSalesPortalConfiguration(
    gymData?.data.salesPortalConfigurationId,
  );

  const salesPortalConfiguration = data?.data;

  const initialValues = useMemo((): FormValues => {
    return {
      enabled: !!salesPortalConfiguration?.enabled,
      slug: salesPortalConfiguration?.slug || '',
      homePageText: salesPortalConfiguration?.homePageText || '',
      facebookTracking: salesPortalConfiguration?.facebookTracking || '',
      googleTracking: salesPortalConfiguration?.googleTracking || '',
      file: null,
    };
  }, [salesPortalConfiguration]);

  const form = useForm<FormValues>({
    initialValues,
    validate: values => ({
      slug: !values.slug ? 'Blank url not permitted' : null,
    }),
  });

  const handleEnable = (e: React.ChangeEvent<HTMLInputElement>): void => {
    form.setFieldValue('enabled', e.currentTarget.checked);
  };

  const handleImageChange = (file: File): void => {
    setUploading(true);
    if (file && file.size > 5000000) {
      errorNotification('The selected file is too large. Please select a file smaller than 5MB.');
    } else if (file) {
      setLogo(URL.createObjectURL(file));
      form.setValues({ ...form.values, file });
    }
    setUploading(false);
  };

  const handleSubmit = (values: FormValues): void => {
    if (!!salesPortalConfiguration) {
      const payload = {
        ...values,
        enabled: values.enabled ? 'True' : 'False',
        salesPortalConfigurationId: salesPortalConfiguration?.salesPortalConfigurationId,
      };
      updateMutation.mutate(payload, {
        onError: error => {
          errorNotification('Sales portal update failed', error.response?.data.message);
        },
        onSuccess: () => {
          successNotification('Sales portal update success');
          close();
        },
      });
    }
  };

  const handleCancelChanges = (): void => {
    setLogo(salesPortalConfiguration?.logo || null);
    form.setValues(initialValues);
    close();
  };

  useShallowEffect(() => {
    if (salesPortalConfiguration && isSuccess) {
      setLogo(salesPortalConfiguration.logo);
      form.setValues(initialValues);
    }
  }, [salesPortalConfiguration]);

  return (
    <>
      <PageHeader>
        <Title order={1}>Sales Portal</Title>
      </PageHeader>
      <LoadingOverlay visible={isLoading} />
      <Stack p={px(theme.spacing.xl) * 2} sx={{ overflowY: 'scroll', flexGrow: 1 }}>
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Text>
            Your Sales Portal can be used to list and sell your packages to prospects and gain new
            members. Existing athletes can use the sales portal as a self-serve way to purchase packages. Use
            this page to enable your sales portal and customize its appearance.
          </Text>
          <Group position='apart'>
            <div />
            <Group spacing='xs'>
              {isEditable ? (
                <>
                  <Button color='red' onClick={handleCancelChanges} disabled={updateMutation.isLoading}>
                    Cancel
                  </Button>
                  <Button
                    type='submit'
                    color='green'
                    loading={updateMutation.isLoading || uploading}
                    disabled={!permission}
                    data-cy='saveSalesPortalConfigButton'
                  >
                    Save
                  </Button>
                </>
              ) : (
                <Button
                  onClick={open}
                  disabled={!permission}
                  loading={isLoading || updateMutation.isLoading}
                >
                  Edit
                </Button>
              )}
            </Group>
          </Group>
          <Checkbox
            checked={form.values.enabled}
            disabled={!permission || !isEditable}
            {...form.getInputProps('enabled')}
            onChange={handleEnable}
            label='Enable Sales Portal'
            pb={theme.spacing.lg}
            data-cy='enableSalesPortalCheckbox'
          />
          {form.values.enabled && (
            <Group noWrap align='center' sx={{ gap: 0 }}>
              <Input.Label ta='right' my='0.25rem' pr={theme.spacing.xl}>
                URL
              </Input.Label>
              {isEditable ? (
                <>
                  <Text fs='italic' c='dimmed' pr='sm'>
                    {`${salesPortalConfiguration?.salesPortalUrl.split('/').slice(0, -1).join('/')}/`}
                  </Text>
                  <TextInput
                    disabled={!isEditable}
                    size='xs'
                    placeholder='Enter url'
                    {...form.getInputProps('slug')}
                    w={px(theme.spacing.xl) * 20}
                    data-cy='salesPortalSlugInput'
                  />
                </>
              ) : (
                <Text fs='italic' c='dimmed'>
                  {salesPortalConfiguration?.salesPortalUrl}
                </Text>
              )}
              <Button
                compact
                ml={theme.spacing.xl}
                disabled={isEditable || !salesPortalConfiguration?.enabled}
                onClick={() => {
                  if (data?.data) {
                    navigator.clipboard.writeText(data?.data.salesPortalUrl);
                    successNotification('Copied URL to clipboard');
                  }
                }}
              >
                Copy URL
              </Button>
            </Group>
          )}
          {form.values.enabled && (
            <Group align='left' sx={{ gap: 0 }} pt={theme.spacing.lg}>
              <Input.Label ta='right' my='0.25rem' pr={theme.spacing.xl}>
                Logo Image
              </Input.Label>
              <Stack my='xs'>
                <ImageUpload
                  handleFileChange={files => handleImageChange(files[0])}
                  disabled={!permission || !isEditable}
                  imgSrc={logo || ''}
                  loading={uploading}
                  width={px(theme.spacing.xl) * 12}
                  height={px(theme.spacing.xl) * 8}
                />
              </Stack>
            </Group>
          )}
          {form.values.enabled && (
            <Group noWrap align='left' sx={{ gap: 0 }} pt={theme.spacing.lg}>
              <Input.Label ta='right' my='0.25rem' pr={theme.spacing.xl}>
                Home Page Description
              </Input.Label>
              <Textarea
                disabled={!isEditable}
                size='xs'
                placeholder='Enter home page text'
                {...form.getInputProps('homePageText')}
                w={px(theme.spacing.xl) * 20}
                data-cy='homePageTextInput'
              />
            </Group>
          )}
          {/*
            ENG-2598: the Facebook / Google Pixel Tracking inputs are hidden until product
            specifies how the tracking should work. No page of the sales portal has ever
            rendered these values, so the inputs only ever promised tracking that did not
            happen (SUPP-1454).
          */}
        </form>
      </Stack>
    </>
  );
};

export default SalesPortalConfiguration;
